import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, clearSession } from "../lib/api";
import Logo from "../components/Logo";
import { useAuth } from "../lib/auth";

function parseRecoveryFromUrl() {
  if (typeof window === "undefined") return { token: null, error: null };
  const hash = window.location.hash?.replace(/^#/, "") || "";
  const search = window.location.search?.replace(/^\?/, "") || "";
  const params = new URLSearchParams(hash || search);
  const errDesc = params.get("error_description") || params.get("error");
  if (errDesc) return { token: null, error: decodeURIComponent(errDesc) };
  const t = params.get("access_token");
  const type = params.get("type");
  if (t && (!type || type === "recovery")) return { token: t, error: null };
  return { token: null, error: null };
}

export default function ResetPassword() {
  const nav = useNavigate();
  const { user } = useAuth();
  // Parse token synchronously so StrictMode's double-effect can't race with hash cleanup.
  const [initial] = useState(() => parseRecoveryFromUrl());
  const [accessToken] = useState(initial.token);
  const [invalid] = useState(!initial.token);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const cleanedRef = useRef(false);

  useEffect(() => {
    if (cleanedRef.current) return;
    cleanedRef.current = true;
    if (initial.error) toast.error(initial.error);
    // Rimuovi il token dall'URL dopo averlo catturato in state
    if (window.history.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [initial.error]);

  if (user && !accessToken) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri.");
      return;
    }
    if (password !== confirm) {
      toast.error("Le due password non coincidono.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { access_token: accessToken, new_password: password });
      toast.success("Password aggiornata. Ora puoi accedere.");
      clearSession();
      localStorage.removeItem("maven_session");
      setTimeout(() => nav("/login", { replace: true }), 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile aggiornare la password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F9FC] px-4 py-8">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-6 flex flex-col items-center">
            <Logo className="h-24 w-auto sm:h-28" />
          </div>

          <h1 className="text-xl font-bold tracking-tight text-[#1F2937] sm:text-2xl">
            Imposta una nuova password
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            {invalid
              ? "Il link non è valido o è scaduto. Richiedine uno nuovo dalla schermata di login."
              : "Inserisci la nuova password del tuo account Maven Academy."}
          </p>

          {!invalid && (
            <form onSubmit={submit} noValidate className="mt-6 space-y-4" data-testid="reset-form">
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#1F2937]" htmlFor="np">
                  Nuova password
                </label>
                <input
                  id="np"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="maven-input"
                  placeholder="Almeno 8 caratteri"
                  autoComplete="new-password"
                  data-testid="reset-new-password"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#1F2937]" htmlFor="cp">
                  Conferma nuova password
                </label>
                <input
                  id="cp"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="maven-input"
                  placeholder="Ripeti la password"
                  autoComplete="new-password"
                  data-testid="reset-confirm-password"
                />
              </div>
              <button
                type="submit"
                disabled={busy || !accessToken}
                className="maven-btn-primary w-full"
                data-testid="reset-submit-button"
              >
                {busy ? "Aggiorno..." : "Aggiorna password"}
              </button>
            </form>
          )}

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => nav("/login", { replace: true })}
              className="text-xs font-semibold text-[#0153A3] hover:underline"
              data-testid="reset-back-login"
            >
              Torna al login
            </button>
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-[#9CA3AF]">
          Maven Academy · Piattaforma interna Maven Pharma
        </p>
      </div>
    </div>
  );
}
